import { useMemo } from "react";
import { Column, useSortBy, UseSortByColumnProps, useTable } from "react-table";
import CheckBox from "@avinet/adaptive-ui-core/form/controls/CheckBox";
import FormProvider from "@avinet/adaptive-ui-core/form/FormProvider";
import useForm from "@avinet/adaptive-ui-core/form/useForm";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import useSessionToken from "../../hooks/useSessionToken";
import { InputSection, InputSectionType } from "../../types/Section";
import { formatDate } from "../../utils/date";
import { Icon, IconEnum } from "../Icon";
import EditableCell from "./EditableCell";
import { formatValue } from "./formatValue";
import { ResultsFilters, useFilteredData } from "./Results";
import "./ResultsTable.scss";

const smileys: IconEnum[][] = [
  ["emojiSad", "emojiLaugh"],
  ["emojiSad", "emojiMeh", "emojiLaugh"],
  ["emojiSad", "emojiMeh", "emojiSmileSlight", "emojiLaugh"],
  ["emojiAngry", "emojiSad", "emojiMeh", "emojiSmileSlight", "emojiLaugh"],
];

export function getRatingLabel(
  symbol: "heart" | "star" | "smiley",
  value: number,
  max: number
) {
  switch (symbol) {
    case "smiley":
      return (
        <span title={value.toString()}>
          <Icon name={smileys[max - 2][value - 1]} />
        </span>
      );

    default:
      return (
        <span title={value.toString()}>
          {Array.from(" ".repeat(value)).map((_v, i) => (
            <Icon key={i} name={symbol} />
          ))}
        </span>
      );
  }
}

function includeSection(section: InputSection) {
  switch (section.type) {
    case InputSectionType.boolean:
      return !section.required;

    default:
      return true;
  }
}

function sort(section: InputSection, a: any, b: any): 0 | -1 | 1 {
  const av = a.original[section.columnName] ?? null;
  const bv = b.original[section.columnName] ?? null;

  if (section.type === InputSectionType.boolean) {
    if (!!av !== !!bv) {
      if (!!av) return -1;
      return 1;
    }
    return 0;
  }

  if (av !== bv) {
    if (av === null) return -1;
    if (bv === null) return 1;

    switch (section.type) {
      case InputSectionType.integer:
      case InputSectionType.decimal:
      case InputSectionType.date:
        const ad = new Date(av);
        const bd = new Date(bv);
        if (ad < bd) return -1;
        return 1;

      default:
        const as = av.toString();
        const bs = bv.toString();
        const r = as.localeCompare(bs);
        return r > 0 ? 1 : r < 0 ? -1 : 0;
    }
  }
  return 0;
}

const ResultsTable = ({
  uuid,
  data,
  filters,
  inputSections,
}: {
  uuid: string;
  data: any[];
  inputSections: InputSection[];
  filters: ResultsFilters;
}) => {
  const t = useTranslate("Results");
  const sessionToken = useSessionToken();
  const filteredData = useFilteredData(null, filters, data);

  const filteredSections = useMemo(
    () => inputSections.filter(includeSection),
    [inputSections]
  );

  const columns = useMemo(() => {
    const columns: Column<any>[] = [
      {
        id: "date_created",
        accessor: "date_created",
        Header: t("created"),
        Cell: (cell) => formatDate(cell.value),
      },
    ];
    return columns.concat(
      filteredSections.map((section) => ({
        id: section.columnName,
        Header: (
          <>
            {section.label}
            {section.administrative && (
              <>
                <br />
                {t("admin")}
              </>
            )}
          </>
        ),
        accessor: section.columnName,
        className: section.type,
        editable: !!section.administrative,
        Cell: (cell) =>
          section.administrative ? (
            <EditableCell
              uuid={uuid}
              section={section}
              record={cell.row.original}
              value={cell.value}
            />
          ) : (
            formatValue(section, cell.value, t, sessionToken)
          ),
        sortType: (a: any, b: any) => sort(section, a, b),
      }))
    );
  }, [filteredSections, t]);

  const table = useTable(
    { data: filteredData, columns, autoResetSortBy: false } as any,
    useSortBy
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    table;

  const { form, state } = useForm<{ includeInPrint: boolean }>({
    defaultValues: { includeInPrint: true },
  });

  return (
    <div
      className={"results-table" + (state.includeInPrint ? "" : " no-print")}
    >
      <table cellPadding={0} cellSpacing={0} {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...props } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} {...props}>
                {headerGroup.headers.map((header) => {
                  const editable = (header as unknown as { editable: boolean })
                    .editable as boolean;
                  const { key, ...props } = header.getHeaderProps({
                    className:
                      (header as any).className + (editable ? " editable" : ""),
                  });
                  const sortedHeader =
                    header as unknown as UseSortByColumnProps<any>;
                  return (
                    <th
                      key={key}
                      {...props}
                      {...(sortedHeader as any).getSortByToggleProps()}
                    >
                      {header.render("Header")}
                      {sortedHeader.isSorted ? (
                        <Icon
                          name="chevron"
                          rotate={sortedHeader.isSortedDesc ? 90 : 270}
                        />
                      ) : (
                        ""
                      )}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);

            const { key, ...props } = row.getRowProps({});
            return (
              <tr key={key} {...props}>
                {row.cells.map((cell) => {
                  const { key, ...props } = cell.getCellProps({
                    className:
                      (cell.column as any).className +
                      ((cell.column as any).editable ? " editable" : ""),
                  });
                  return (
                    <td key={key} {...props}>
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <br />
      <FormProvider state={state} form={form}>
        <CheckBox
          className="no-print"
          id="includeInPrint"
          label={t("includeInPrint")}
        />
      </FormProvider>
    </div>
  );
};

export default ResultsTable;
